import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setPerceivedAromaticity, setPerceivedAtomStereo } from '@peruselab/structure-editor/domain/graphDrawing';
import type { DrawingDocument } from '../generated/drawing-contract';
import {
  chemicalSourceKey,
  validateChemicalDocument,
  type ChemicalValidationResult
} from '@peruselab/structure-editor/chemistry/chemicalValidation';

type ValidationState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading'; readonly sourceKey: string }
  | { readonly state: 'ready'; readonly result: ChemicalValidationResult }
  | { readonly state: 'error'; readonly sourceKey: string; readonly message: string };

export function useChemicalValidation(
  document: DrawingDocument,
  applyWithoutHistory: (change: (current: DrawingDocument) => DrawingDocument) => void
) {
  const [validation, setValidation] = useState<ValidationState>({ state: 'idle' });
  const validationRequestRef = useRef(0);
  const sourceKey = chemicalSourceKey(document);

  const currentValidation =
    validation.state === 'ready' && validation.result.sourceKey === sourceKey ? validation.result : null;
  const validationIsStale =
    validation.state === 'ready'
      ? validation.result.sourceKey !== sourceKey
      : validation.state === 'error' && validation.sourceKey !== sourceKey;

  useEffect(() => {
    if (currentValidation?.status !== 'valid') return;
    applyWithoutHistory((current) => {
      const withAromaticity = setPerceivedAromaticity(
        current,
        currentValidation.aromaticity.atomIds,
        currentValidation.aromaticity.bondIds
      );
      return setPerceivedAtomStereo(
        withAromaticity,
        currentValidation.stereochemistry.atomValues,
        currentValidation.stereochemistry.bondValues
      );
    });
  }, [applyWithoutHistory, currentValidation]);

  const invalidAtomIds = useMemo(
    () =>
      new Set(
        currentValidation?.issues.flatMap((issue) =>
          issue.targetType === 'atom' && issue.targetId ? [issue.targetId] : []
        ) ?? []
      ),
    [currentValidation]
  );
  const invalidBondIds = useMemo(
    () =>
      new Set(
        currentValidation?.issues.flatMap((issue) =>
          issue.targetType === 'bond' && issue.targetId ? [issue.targetId] : []
        ) ?? []
      ),
    [currentValidation]
  );

  const runValidation = useCallback(async () => {
    if (document.structure.graph.nodes.length === 0) {
      setValidation({ state: 'idle' });
      return;
    }
    const requestId = validationRequestRef.current + 1;
    validationRequestRef.current = requestId;
    const requestedSourceKey = chemicalSourceKey(document);
    setValidation({ state: 'loading', sourceKey: requestedSourceKey });
    try {
      const result = await validateChemicalDocument(document);
      if (validationRequestRef.current === requestId) {
        setValidation({ state: 'ready', result });
      }
    } catch (error) {
      if (validationRequestRef.current === requestId) {
        setValidation({
          state: 'error',
          sourceKey: requestedSourceKey,
          message: error instanceof Error ? error.message : 'Chemical validation failed.'
        });
      }
    }
  }, [document]);

  useEffect(() => {
    if (document.structure.graph.nodes.length === 0) {
      validationRequestRef.current += 1;
      setValidation({ state: 'idle' });
      return;
    }
    const timeout = globalThis.setTimeout(() => void runValidation(), 300);
    return () => globalThis.clearTimeout(timeout);
  }, [document.structure.graph.nodes.length, runValidation, sourceKey]);

  return {
    validation,
    currentValidation,
    validationIsStale,
    invalidAtomIds,
    invalidBondIds,
    runValidation
  };
}
